/**
 * Global route-level error boundary — a React render error anywhere in a
 * page previously white-screened the ENTIRE app (real bug June 2026: the
 * schedule-post "photo area" crash took the whole site down for an owner).
 * `resetKey` (the current pathname) auto-clears the error on navigation.
 */
import { Component } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    try {
      console.error("[ErrorBoundary]", error, info?.componentStack);
    } catch {
      /* noop */
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary-fallback">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              That page hit an unexpected error. Your data is safe — try
              reloading, or head back to your dashboard.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                data-testid="error-boundary-reload"
                className="px-4 py-2 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90"
              >
                Reload page
              </button>
              <a
                href="/dashboard"
                data-testid="error-boundary-home"
                className="px-4 py-2 rounded-md border text-sm font-medium hover:bg-muted"
              >
                Go to dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
